import type { Navigator, Screen } from "../app/screen";
import { Api } from "../net/api";
import { pageShell, note } from "../ui/page";
import { LOYALTY, LOYALTY_PENALTY, type Loyalty } from "../loyalty.generated";
import { REALM_COLOUR } from "../card-frames.generated";
import { startLobbyTour } from "../tutorial";

/**
 * HELP - how the game is played, generated from the game itself.
 *
 * ⭐ **NOTHING HERE IS WRITTEN TWICE.** The keyword glossary is the served `keywords[]`, the deck
 * limits are `GameConfig`, and the loyalty wheel is the generated table the deck builder and the
 * engine both use. A help page that RESTATES the rules is a second owner of them, and it goes stale
 * the first time a number is balanced - which is exactly when a player is most likely to read it.
 *
 * ⭐ **THE THREE THINGS A NEW PLAYER CANNOT DEDUCE**, in the order they will need them:
 *   1. the turn - what Foxfire is and how it grows
 *   2. the realms - why the same card costs more in one deck than another
 *   3. the keywords - what the words on the cards do
 *
 * ⚠️ **THE GLOSSARY IS DE-DUPLICATED BY TITLE.** The database defines 146 keyword entries and many
 * share a name with different values (`Heal` appears six times, once per variant). Listing all of
 * them is a wall a player will not read; listing the distinct NAMES with the clearest description is
 * a glossary.
 */
export const HelpScreen: Screen = {
  id: "help",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "HOW TO PLAY", "the rules, from the game itself");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "help";
    shell.body.append(root);

    // ── the turn ─────────────────────────────────────────────────────────────────────────────────
    let cfg: Record<string, unknown> = {};
    try {
      cfg = await Api.gameConfig() as Record<string, unknown>;
    } catch {
      // Non-fatal: the glossary and the wheel are still worth showing.
    }

    // ⭐ **THE RULES FIRST, THE TOUR SECOND.** Alex, 2026-08-15: *"the how to play tutorial is
    // horrible. Also there is no explanation of the game rules anywhere, what foxfire is, how can a
    // player come here and know how to play this game."* Both halves are the same mistake: HOW TO
    // PLAY led straight into a spotlight tour of the LOBBY - which points at buttons and never
    // explains a single rule - while this screen, which explains the turn, Foxfire, the loyalty
    // wheel and every keyword, was a small utility link in the footer beside SHOP and SETTINGS.
    //
    // A person who has never seen a card game needs the RULES. The tour is worth having once they
    // know what they are looking at, so it is offered from here rather than instead of here.
    const tourRow = document.createElement("div");
    tourRow.className = "help__tour";
    const tourLead = document.createElement("span");
    tourLead.className = "help__tourlead";
    tourLead.textContent = "Read this first. When you want to see it on screen:";
    const tourBtn = document.createElement("button");
    tourBtn.type = "button";
    tourBtn.className = "utilbtn";
    tourBtn.dataset["label"] = "SHOW ME AROUND";
    tourBtn.textContent = "SHOW ME AROUND";
    tourBtn.addEventListener("click", () => { void startLobbyTour(); });
    tourRow.append(tourLead, tourBtn);
    shell.body.append(tourRow);

    const turn = section("THE TURN");
    turn.body.append(
      para(`Each player opens with ${n(cfg["handCardsStartCount"], 5)} cards and may swap any of ` +
           `them once, before the first turn. That is your only mulligan.`),
      para("On your turn you spend FOXFIRE to play cards, then your creatures may attack. A " +
           "creature cannot attack on the turn it arrives."),
      para("Feed the Foxfire by burning a card from your hand into the basin at the centre of the " +
           "arena. A burned card is gone; what you get back is a token of that card's realm."),
      para(`You may hold ${n(cfg["handCardsLimit"], 7)} cards and field ` +
           `${n(cfg["maxCardsOnTable"], 7)} creatures. Reduce the enemy hero to zero to win.`),
    );

    // ── the realms ───────────────────────────────────────────────────────────────────────────────
    const realms = section("THE REALMS");
    realms.body.append(
      para("Every card belongs to a realm, and so does every hero. A card's standing with YOUR " +
           "hero decides what it costs you - and whether you may take it at all."),
    );

    const wheel = document.createElement("div");
    wheel.className = "help__wheel";
    for (const hero of Object.keys(LOYALTY)) {
      const row = document.createElement("div");
      row.className = "help__wheelrow";

      const name = document.createElement("span");
      name.className = "help__realm";
      name.textContent = pretty(hero);
      if (REALM_COLOUR[hero]) name.style.color = REALM_COLOUR[hero]!;
      row.append(name);

      const rels = document.createElement("span");
      rels.className = "help__rels";
      // ⚠️ Ordered by severity, not by realm name: a player reads this to find the one they cannot
      // take, and that must be the easiest thing on the line to find.
      const order: Loyalty[] = ["Tolerated", "Hostile", "Nemesis"];
      const entries = Object.entries(LOYALTY[hero] ?? {});
      for (const level of order) {
        for (const [realm, l] of entries) {
          if (l !== level) continue;
          const tag = document.createElement("span");
          tag.className = `help__rel help__rel--${level.toLowerCase()}`;
          const cost = level === "Nemesis"
            ? "cannot be used"
            : `+${LOYALTY_PENALTY[level]} foxfire`;
          tag.textContent = `${pretty(realm)}: ${cost}`;
          rels.append(tag);
        }
      }
      row.append(rels);
      wheel.append(row);
    }
    realms.body.append(wheel);
    realms.body.append(
      para("Cards of your own realm, and neutral cards, always cost their printed price."),
    );

    // ── deck building ────────────────────────────────────────────────────────────────────────────
    const decks = section("BUILDING A DECK");
    decks.body.append(
      para(`A deck holds ${n(cfg["minCardsInDeck"], 30)} to ${n(cfg["maxCardsInDeck"], 35)} cards, ` +
           `and you may keep ${n(cfg["maxDeckCount"], 6)} decks. Each card has its own limit on ` +
           `how many copies you may include.`),
      para("You cannot include a card from your hero's nemesis realm. The builder greys those out " +
           "rather than hiding them, so you can see what your choice of hero costs you."),
    );

    // ── the glossary ─────────────────────────────────────────────────────────────────────────────
    const glossary = section("THE WORDS ON THE CARDS");
    const list = document.createElement("dl");
    list.className = "help__glossary";
    glossary.body.append(list);
    list.append(note("Loading..."));

    root.append(turn.el, realms.el, decks.el, glossary.el);

    try {
      const db = await Api.gameDatabase() as {
        keywords?: Array<{ title?: string; description?: string }>;
      };
      const seen = new Map<string, string>();
      for (const k of db.keywords ?? []) {
        const title = (k?.title ?? "").trim();
        const desc = (k?.description ?? "").trim();
        // ⚠️ `None` is the engine's own "do not display" entry, and it says so in its description.
        if (!title || !desc || title === "None") continue;
        const prev = seen.get(title);
        // Keep the clearest wording: the longest description that is not a template of values.
        if (!prev || (desc.length > prev.length && !desc.includes("{value"))) {
          seen.set(title, desc);
        }
      }

      list.replaceChildren();
      for (const title of [...seen.keys()].sort((a, b) => a.localeCompare(b))) {
        const dt = document.createElement("dt");
        dt.className = "help__term";
        dt.textContent = title;
        const dd = document.createElement("dd");
        dd.className = "help__def";
        // ⚠️ textContent: this is served data, and served data is not markup.
        dd.textContent = seen.get(title) ?? "";
        list.append(dt, dd);
      }
      if (!seen.size) list.append(note("The glossary is empty.", true));
    } catch (e) {
      list.replaceChildren(note(`Could not load the glossary: ${describe(e)}`, true));
    }
  },
};

function section(title: string): { el: HTMLElement; body: HTMLElement } {
  const el = document.createElement("section");
  el.className = "help__section";
  const h = document.createElement("h2");
  h.className = "help__head";
  h.textContent = title;
  const body = document.createElement("div");
  body.className = "help__body";
  el.append(h, body);
  return { el, body };
}

function para(text: string): HTMLElement {
  const p = document.createElement("p");
  p.className = "help__para";
  p.textContent = text;
  return p;
}

/** `The_Verdant_Hollow` -> `The Verdant Hollow`. */
function pretty(realm: string): string {
  return realm.replace(/_/g, " ");
}

function n(v: unknown, fallback: number): number {
  const x = typeof v === "number" ? v : Number(v);
  return Number.isFinite(x) ? x : fallback;
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
